import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { Amount, ICustomerTrip, ICustomerTripsResponse, ITripBilling } from '../../models/customer-data.interface';
import { RootState } from '../store';
import * as customerSupportApi from '../../apis/customer-support-api';
import { CustomerState } from './customer-state';
import { Action } from '../types';
import { FilterByDateRange } from './types';
import { FilterTripHistoryServiceProviderTypes, TripServiceProvider } from '../../components/details-view/trip-history/types';
import { resetErrorDialog, setErrorDialog } from './notification-slice';
import { FilterTripHistoryPaymentState } from '../../utils/trip-invoice-utils';

export const selectTrips = () => (state: RootState) => {
    return state.customer.trips;
};

// ## Fetch Customer Trips
const thunkHandler = async ({ id }, { dispatch, rejectWithValue }) => {
    dispatch(resetErrorDialog());

    try {
        const result: ICustomerTripsResponse = await customerSupportApi.getCustomerTrips(id);
        return result.data;
    } catch (error) {
        dispatch(setErrorDialog({
            code: 'UNABLE_TO_FETCH_TRIPS',
            title: 'Unable to fetch Trips',
            message: 'An error occurred: Could not fetch the trips of the customer.'
        }));

        return rejectWithValue(error.message);
    }

    //return result.data.sort((e1, e2) => {
    //    return new Date(e2.tripPeriod?.startTimestamp || 0).valueOf() - new Date(e1.tripPeriod?.startTimestamp || 0).valueOf()
    //});
}

const thunkOptions = {
    condition: (refresh, { getState }) => {
        // execute the function if it's explicitly requested
        if (refresh) {
            return true;
        }

        const { customer } = getState();
        return customer.trips.status === 'idle'; // execute the fetch function only when the status is idle
    },
}

export const fetchCustomerTrips = createAsyncThunk<ICustomerTrip[], { id: string }, { state: RootState }>('customer/fetchCustomerTrips', thunkHandler, thunkOptions);

// ## Refund trip

const refundTripThunkHandler = async ({ trippaymentId, serviceProviderId, refundAmount }, { dispatch, rejectWithValue }) => {
    dispatch(resetErrorDialog());

    try {
        const response = await customerSupportApi.refundCustomerTrip(trippaymentId, serviceProviderId, refundAmount)

        console.log('response.data', response.data);
        return response.data;
    } catch (error) {
        dispatch(setErrorDialog({
            message: 'An error occurred: Could not refund the customer trip.',
            code: 'UNABLE_TO_REFUND_TRIP',
            title: 'Unable to refund Trip',
        }));

        return rejectWithValue(error.message);
    }
}

const refundTripThunkOptions = {
    condition: (refresh, { getState }) => {
        // execute the function if it's explicitly requested
        if (refresh) {
            return true;
        }

        const { customer } = getState();
        console.log('customer', customer)
        return customer.trips.status === 'idle'; // execute the fetch function only when the status is idle
    },
}

export const refundCustomerTrip = createAsyncThunk<ITripBilling, { trippaymentId: string, serviceProviderId: string, refundAmount: Amount }, { state: RootState }>(
    'customer/refundCustomerTrip',
    refundTripThunkHandler, refundTripThunkOptions
);

// #

const fetchTripPaymentHandler = async ({ tripId, customerId }, { dispatch, rejectWithValue }) => {
    dispatch(resetErrorDialog());

    try {
        const response = await customerSupportApi.getCustomerTrip(customerId, tripId);

        console.log('response.data', response.data);
        return response.data;
    } catch (error) {
        dispatch(setErrorDialog({
            message: 'An error occurred: Could not fetch the customer trip payment.',
            code: 'UNABLE_TO_FETCH_TRIP_PAYMENT',
            title: 'Unable to fetch Trip payment',
        }));

        return rejectWithValue(error.message);
    }
}

const fetchTripPaymentThunkOptions = {
    condition: (refresh, { getState }) => {
        // execute the function if it's explicitly requested
        if (refresh) {
            return true;
        }

        const { customer } = getState();

        return customer.trips.status === 'idle'; // execute the fetch function only when the status is idle
    },
}

export const fetchTripPayment = createAsyncThunk<ICustomerTrip, { tripId: string, customerId: string }, { state: RootState }>(
    'customer/fetchTripPayment',
    fetchTripPaymentHandler, fetchTripPaymentThunkOptions
);


export function addTripsCases(builder: ActionReducerMapBuilder<CustomerState>) {
    builder
        .addCase(fetchCustomerTrips.pending, (state) => {
            state.trips.status = 'loading';
        })
        .addCase(fetchCustomerTrips.fulfilled, (state, action) => {
            state.trips.data = state.trips.presentationData = state.trips.filteredData = action.payload;
            state.trips.status = 'succeeded';
        })
        .addCase(fetchCustomerTrips.rejected, (state) => {
            state.id = undefined;
            state.trips.status = 'failed';
        })

        .addCase(fetchTripPayment.pending, (state) => {
            state.trips.status = 'loading';
        })
        .addCase(fetchTripPayment.fulfilled, (state, action) => {
            const tripId = action.payload.id;

            const tripsIndex = state.trips.data?.findIndex(t => t.id === tripId);
            if (tripsIndex !== -1) state.trips.data[tripsIndex] = action.payload;

            const presentationIndex = state.trips.presentationData?.findIndex(t => t.id === tripId);
            if (presentationIndex !== -1) state.trips.presentationData[presentationIndex] = action.payload;

            const filteredIndex = state.trips.filteredData?.findIndex(t => t.id === tripId);
            if (filteredIndex !== -1) state.trips.filteredData[filteredIndex] = action.payload;

            state.trips.status = 'succeeded';
        })
        .addCase(fetchTripPayment.rejected, (state) => {
            state.id = undefined;
            state.trips.status = 'failed';
        })

        .addCase(refundCustomerTrip.pending, (state) => {
            state.trips.status = 'loading';
        })
        .addCase(refundCustomerTrip.fulfilled, (state, action) => {
            state.trips.status = 'succeeded';

            const response = action.payload;

            /*
            const tripTotalAmount = response.billings.filter(b => b.type !== 'REFUND' && b.state === 'COMPLETED').reduce((acc, val) => acc += (val?.totalAmount?.grossAmount || 0), 0);
            const refundedTotalAmount = response.billings.filter(b => b.type === 'REFUND' && (b.state === 'COMPLETED' || b.state === 'INITIATED')).reduce((acc, val) => acc += (val?.refundAmount?.grossAmount || 0), 0);
            const decimalTotalAmount = Math.round((tripTotalAmount - refundedTotalAmount) * 100) / 100;

            const isRefunded = decimalTotalAmount === 0;
            */

            const tripIndex = state.trips.data.findIndex(trip => trip.id === response.referenceId);
            const presentationDataIndex = state.trips.presentationData.findIndex(trip => trip.id === response.referenceId);
            const filteredDataIndex = state.trips.filteredData.findIndex(trip => trip.id === response.referenceId);

            state.trips.data[tripIndex] = {
                ...state.trips.data[tripIndex],
                billings: [...response.billings],
            };
            state.trips.presentationData[presentationDataIndex] = {
                ...state.trips.presentationData[presentationDataIndex],
                billings: [...response.billings]
            };
            state.trips.filteredData[filteredDataIndex] = {
                ...state.trips.filteredData[filteredDataIndex],
                billings: [...response.billings]
            };

        })
        .addCase(refundCustomerTrip.rejected, (state) => {
            state.id = undefined;
            state.trips.status = 'failed';
        })
}

export const tripsReducers = {
    setTripServiceProvider(state: CustomerState, action: Action<FilterTripHistoryServiceProviderTypes>) {
        const serviceProvider = action.payload
        state.trips.filter.serviceProvider = serviceProvider;
        const action2: Action<FilterByDateRange> = {
            payload: state.trips.filter.dateRange,
            type: ''
        };

        tripsReducers.setTripsDateRageFilter(state, action2);

        if (serviceProvider === 'ALL') {
            return
        }

        const filteredTrips = state.trips.presentationData.filter(item => {
            return item.serviceProviderId === TripServiceProvider[serviceProvider];
        })

        state.trips.presentationData = filteredTrips;
        state.trips.filteredData = filteredTrips;
    },
    setTripsPaymentState(state: CustomerState, action: Action<FilterTripHistoryPaymentState>) {
        const paymentState = action.payload
        state.trips.filter.paymentState = paymentState;

        if (paymentState === 'ALL') {
            if (!state.trips.filter.serviceProvider) {
                state.trips.presentationData = state.trips.data;
                state.trips.filteredData = state.trips.data;
                return;
            }

            const action: Action<FilterTripHistoryServiceProviderTypes> = {
                payload: state.trips.filter.serviceProvider,
                type: ''
            };

            tripsReducers.setTripServiceProvider(state, action);
            return
        }

        if (paymentState === 'PAID') {
            state.trips.presentationData = state.trips.filteredData.filter(item => {
                const filterFlag = item.billings.findIndex(item => item.state !== 'COMPLETED' && (item.type === 'CHARGE' || item.type === 'FEE')) === -1;
                return filterFlag;
            })
        } else if (paymentState === 'UNPAID') {
            state.trips.presentationData = state.trips.filteredData.filter(item => {
                const filterFlag = item.billings.findIndex(item => item.state !== 'COMPLETED' && (item.type === 'CHARGE' || item.type === 'FEE')) > -1;
                return filterFlag;
            })
        } else if (paymentState === 'CANCELED') {
            state.trips.presentationData = state.trips.filteredData.filter(item => {
                const filterFlag = item.billings.findIndex(item => item.state === 'CANCELED') > -1;
                return filterFlag;
            })
        } else if (paymentState === 'PRE_AUTH') {
            state.trips.presentationData = state.trips.filteredData.filter(item => {
                const filterFlag = item.billings.findIndex(item => item.state === 'AUTHORIZATION_HOLD') > -1;
                return filterFlag;
            })
        } else if (paymentState === 'REJECTED') {
            state.trips.presentationData = state.trips.filteredData.filter(item => {
                const filterFlag = item.billings.findIndex(item => item.state === 'REJECTED') > -1;
                return filterFlag;
            })
        } else if (paymentState === 'WAIT_FOR_PRE_AUTH') {
            state.trips.presentationData = state.trips.filteredData.filter(item => {
                const filterFlag = item.billings.findIndex(item => item.state === 'AWAITING_AUTHORIZATION') > -1;
                return filterFlag;
            })
        }
    },
    setTripsDateRageFilter(state: CustomerState, action: Action<FilterByDateRange>) {
        const dateRange = action.payload
        state.trips.filter.dateRange = dateRange;
        state.trips.filter.paymentState = null;

        if (!dateRange) {
            if (!state.trips.filter.serviceProvider) {
                state.trips.filter.serviceProvider = null;
            }

            state.trips.presentationData = state.trips.data;
            state.trips.filteredData = state.trips.data;
            return
        }

        const startTimestamp = new Date(dateRange.start).getTime();
        const stopTimestamp = new Date(dateRange.stop).getTime();

        let filteredTrips = state.trips.data.filter(item =>
            item.tripPeriod && (
                new Date(item.tripPeriod.startTimestamp).getTime() >= startTimestamp &&
                new Date(item.tripPeriod.endTimestamp).getTime() <= stopTimestamp
            )
        )

        const serviceProvider = state.trips.filter.serviceProvider;
        if (serviceProvider && serviceProvider !== null) {
            filteredTrips = filteredTrips.filter(item => {
                return item.serviceProviderId === TripServiceProvider[serviceProvider];
            })
        }

        state.trips.presentationData = filteredTrips;
        state.trips.filteredData = filteredTrips;
    },
}
